import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, ExternalLink, Send } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import type { ContractStatusKey } from '@pnl/sdk';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';

const STATUS_TONE: Record<ContractStatusKey, 'warning' | 'success' | 'danger'> = {
  PENDING: 'warning',
  SIGNED: 'success',
  REJECTED: 'danger',
};

export function ContractsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ContractStatusKey | 'ALL'>('ALL');
  const [page, setPage] = useState(1);
  const [resendOk, setResendOk] = useState<string | null>(null);
  const [resendErr, setResendErr] = useState<string | null>(null);

  // Checkout abandonado (linha criada no início do checkout que nunca virou
  // compra) fica escondido por padrão — eram 18 em produção, e o cliente via 4
  // no próprio nome. Nunca some em silêncio: a contagem aparece no aviso abaixo.
  const [showAbandoned, setShowAbandoned] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'contracts', { status: statusFilter, page, abandoned: showAbandoned }],
    queryFn: () =>
      api.listContracts({
        page,
        pageSize: 20,
        ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
        ...(showAbandoned ? { abandoned: true } : {}),
      }),
  });

  const resend = useMutation({
    mutationFn: (id: string) => api.resendContract(id),
    onSuccess: (data) => {
      setResendErr(null);
      setResendOk(`Email reenviado para ${data.to}`);
      void qc.invalidateQueries({ queryKey: ['admin', 'contracts'] });
      setTimeout(() => setResendOk(null), 4000);
    },
    onError: (err) => {
      const msg = err instanceof PnlApiError ? err.api.message : 'Falha ao reenviar contrato';
      setResendErr(msg);
      setTimeout(() => setResendErr(null), 5000);
    },
  });

  const retryMissing = useMutation({
    mutationFn: () => api.retryMissingContracts(),
    onSuccess: (r) => {
      setResendErr(null);
      setResendOk(
        r.failed > 0
          ? `${r.ok} de ${r.attempted} gerados. Parou no primeiro erro: ${r.firstError ?? 'desconhecido'}`
          : `${r.ok} contrato(s) gerado(s) — os clientes já receberam o link para assinar.`,
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'contracts'] });
      setTimeout(() => setResendOk(null), 8000);
    },
    onError: (err) => {
      setResendErr(err instanceof PnlApiError ? err.api.message : 'Falha ao gerar os contratos');
      setTimeout(() => setResendErr(null), 6000);
    },
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const abandoned = query.data?.abandoned ?? 0;
  const paidWithoutDocument = query.data?.paidWithoutDocument ?? 0;
  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Operação
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Traders / Contratos
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Todo trader que comprou entra aqui. Acompanhe a assinatura (pendente → assinado) e
            reenvie o documento quando precisar. Depois de assinar, ele pede a ativação no app e
            passa para a fila de Ativações.
          </p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ContractStatusKey | 'ALL');
            setPage(1);
          }}
          className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 rounded-lg bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
        >
          <option value="ALL">Todos</option>
          <option value="PENDING">Pendentes</option>
          <option value="SIGNED">Assinados</option>
          <option value="REJECTED">Rejeitados</option>
        </select>
      </div>

      {resendOk && (
        <Alert tone="success" title="Reenvio realizado">
          {resendOk}
        </Alert>
      )}
      {resendErr && (
        <Alert tone="danger" title="Falha no reenvio">
          {resendErr}
        </Alert>
      )}

      {/* ALERTA: compra paga sem contrato gerado. É falha silenciosa — o cliente
          é ativado mas não tem o que assinar, e ninguém percebe. Aconteceu em
          ago/2026 quando o Autentique ficou sem créditos e 41 clientes travaram;
          só descobrimos porque um deles reclamou. */}
      {paidWithoutDocument > 0 && (
        <Alert tone="danger" title={`${paidWithoutDocument} compra(s) paga(s) sem contrato gerado`}>
          Esses clientes pagaram mas não receberam o contrato para assinar — sem isso não conseguem
          ativar a conta. Costuma ser falha do Autentique (ex.: limite do plano atingido).{' '}
          <button
            type="button"
            onClick={() => retryMissing.mutate()}
            disabled={retryMissing.isPending}
            className="text-danger-700 font-semibold underline disabled:opacity-60"
          >
            {retryMissing.isPending ? 'Gerando…' : 'Gerar agora'}
          </button>
        </Alert>
      )}

      {abandoned > 0 && (
        <Alert tone="info" title={`${abandoned} checkout(s) abandonado(s) fora da lista`}>
          Compras que começaram e não foram concluídas. Cada tentativa de checkout gera uma linha,
          mas nenhum documento chegou a ser emitido — ninguém recebeu e-mail e não há o que assinar.{' '}
          <button
            type="button"
            onClick={() => setShowAbandoned((v) => !v)}
            className="text-brand-600 font-semibold underline"
          >
            {showAbandoned ? 'Ocultar da lista' : 'Mostrar na lista'}
          </button>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Contratos ({total})</CardTitle>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<FileSignature size={28} />}
              title="Nenhum contrato"
              description="Contratos aparecem aqui assim que uma assinatura é paga via Stripe e o Autentique gera o documento."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Trader</TH>
                  <TH>Documento</TH>
                  <TH>Plano</TH>
                  <TH>Status</TH>
                  <TH>Enviado</TH>
                  <TH>Assinado</TH>
                  <TH className="text-right">Ações</TH>
                </TR>
              </THead>
              <TBody>
                {items.map((c) => (
                  <TR key={c.id}>
                    <TD>
                      <div className="text-ink-900 text-sm font-semibold">{c.userFullName}</div>
                      <div className="text-ink-500 text-xs">{c.userEmail}</div>
                    </TD>
                    <TD>
                      <span className="text-ink-700 text-xs">
                        {c.kind === 'FUNDED_CONTRACT' ? 'Contrato Funded Pro' : 'Termo de Compra'}
                      </span>
                    </TD>
                    <TD>
                      {c.planCode ? (
                        <span className="font-mono text-xs">
                          {c.planCode} ({c.marketCode})
                        </span>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </TD>
                    <TD>
                      <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>
                    </TD>
                    <TD>
                      <span className="text-ink-700 text-xs">
                        {new Date(c.createdAt).toLocaleString('pt-BR')}
                      </span>
                    </TD>
                    <TD>
                      <span className="text-ink-700 text-xs">
                        {c.signedAt ? new Date(c.signedAt).toLocaleString('pt-BR') : '—'}
                      </span>
                    </TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-2">
                        {c.autentiqueDocumentId && (
                          <a
                            href={`https://app.autentique.com.br/painel/documentos/abrir/${c.autentiqueDocumentId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-ink-600 hover:text-brand-600 inline-flex items-center gap-1 text-xs"
                          >
                            <ExternalLink size={12} /> Autentique
                          </a>
                        )}
                        {c.status === 'PENDING' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => resend.mutate(c.id)}
                            disabled={resend.isPending}
                          >
                            <Send size={12} />
                            Reenviar
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="text-ink-500 mt-4 flex items-center justify-between text-xs">
              <span>
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
